import type { Editor } from '@tiptap/core'
import { type ReactNode, useCallback, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Dropdown } from '../../general/Dropdown/Dropdown'
import Icon from '../../general/Icon'
import { BadgeCell, LabelCell, MenuPanel, MenuRow } from './DropdownMenu.styles'

export type BlockStyle =
  | 'paragraph'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'bulletList'
  | 'orderedList'

type DropdownValue = BlockStyle

type BlockStyleOption = {
  value: DropdownValue
  badge: ReactNode
  label: string
}

/** Declared before `OPTIONS` so Biome allows JSX in the options list. */
const HeadingBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme }) => theme.color.neutral.color5};
`

const OPTIONS: BlockStyleOption[] = [
  {
    value: 'paragraph',
    badge: <Icon name="AlignLeftOutlined" size={14} />,
    label: 'Normal'
  },
  {
    value: 'h1',
    badge: <HeadingBadge>H1</HeadingBadge>,
    label: 'Heading 1'
  },
  {
    value: 'h2',
    badge: <HeadingBadge>H2</HeadingBadge>,
    label: 'Heading 2'
  },
  {
    value: 'h3',
    badge: <HeadingBadge>H3</HeadingBadge>,
    label: 'Heading 3'
  },
  {
    value: 'bulletList',
    badge: <Icon name="UnorderedListOutlined" size={14} />,
    label: 'Bullet List'
  },
  {
    value: 'orderedList',
    badge: <Icon name="OrderedListOutlined" size={14} />,
    label: 'Numbered List'
  }
]

function getCurrentValue(editor: Editor): DropdownValue {
  if (editor.isActive('bulletList')) {
    return 'bulletList'
  }
  if (editor.isActive('orderedList')) {
    return 'orderedList'
  }
  if (editor.isActive('heading', { level: 1 })) {
    return 'h1'
  }
  if (editor.isActive('heading', { level: 2 })) {
    return 'h2'
  }
  if (editor.isActive('heading', { level: 3 })) {
    return 'h3'
  }
  return 'paragraph'
}

function applyValue(editor: Editor, value: DropdownValue) {
  switch (value) {
    case 'paragraph': {
      let chain = editor.chain().focus()
      if (editor.isActive('bulletList')) {
        chain = chain.toggleBulletList()
      }
      if (editor.isActive('orderedList')) {
        chain = chain.toggleOrderedList()
      }
      chain.setParagraph().run()
      break
    }
    case 'h1':
    case 'h2':
    case 'h3': {
      const level = Number(value.slice(1)) as 1 | 2 | 3
      let chain = editor.chain().focus()
      if (editor.isActive('bulletList')) {
        chain = chain.toggleBulletList()
      }
      if (editor.isActive('orderedList')) {
        chain = chain.toggleOrderedList()
      }
      chain.setHeading({ level }).run()
      break
    }
    case 'bulletList': {
      if (editor.isActive('bulletList')) {
        return
      }
      let chain = editor.chain().focus()
      if (editor.isActive('orderedList')) {
        chain = chain.toggleOrderedList()
      }
      chain.toggleBulletList().run()
      break
    }
    case 'orderedList': {
      if (editor.isActive('orderedList')) {
        return
      }
      let chain = editor.chain().focus()
      if (editor.isActive('bulletList')) {
        chain = chain.toggleBulletList()
      }
      chain.toggleOrderedList().run()
      break
    }
    default: {
      break
    }
  }
}

export type BlockStyleDropdownProps = {
  editor: Editor
  allowedStyles?: BlockStyle[]
}

export function BlockStyleDropdown({
  editor,
  allowedStyles
}: BlockStyleDropdownProps) {
  const theme = useTheme()
  const current = getCurrentValue(editor)

  const visibleOptions = allowedStyles
    ? OPTIONS.filter((o) => allowedStyles.includes(o.value))
    : OPTIONS

  const triggerLabel =
    visibleOptions.find((o) => o.value === current)?.label ?? 'Normal'

  const selectedBg = theme.color.status.secondary

  const [menuOpen, setMenuOpen] = useState(false)
  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null)

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        const { from, to } = editor.state.selection
        savedSelectionRef.current = { from, to }
      }
      setMenuOpen(open)
    },
    [editor]
  )

  const handleSelect = useCallback(
    (value: DropdownValue) => {
      if (savedSelectionRef.current) {
        editor.commands.setTextSelection(savedSelectionRef.current)
        savedSelectionRef.current = null
      }
      applyValue(editor, value)
      setMenuOpen(false)
    },
    [editor]
  )

  return (
    <Dropdown
      open={menuOpen}
      onOpenChange={handleOpenChange}
      trigger={['click']}
      placement="bottomLeft"
      overlayStyle={{ minWidth: 220 }}
      overlay={
        <MenuPanel role="menu" aria-label="Block style">
          {visibleOptions.map((opt) => (
            <MenuRow
              key={opt.value}
              type="button"
              role="menuitem"
              $selected={current === opt.value}
              $selectedBg={selectedBg}
              onClick={() => {
                handleSelect(opt.value)
              }}
            >
              <BadgeCell>{opt.badge}</BadgeCell>
              <LabelCell>{opt.label}</LabelCell>
            </MenuRow>
          ))}
        </MenuPanel>
      }
    >
      <TriggerButton type="button">
        <span>{triggerLabel}</span>
        <Icon name="DownOutlined" size={10} />
      </TriggerButton>
    </Dropdown>
  )
}

const TriggerButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.color.neutral.color11};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  color: ${({ theme }) => theme.color.neutral.color3};
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 400;
  gap: 4px;
  height: 32px;
  padding: 0 10px;

  &:hover {
    background: ${({ theme }) => theme.color.neutral.color8};
    border-color: ${({ theme }) => theme.color.neutral.color6};
  }
`
